import React from "react";
import { LoaderCircle, LockKeyhole, Sparkles, X } from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Button } from "../../../components/button";
import { PasswordInput } from "../shared/components/PasswordInput";
import { useResetPassword } from "./hooks/useResetPassword";
import loginLogo from "../../../assets/page/login_logo.svg";
import loginCircleLeft from "../../../assets/page/login_circle_left.svg";
import loginCircleRight from "../../../assets/page/login_circle_right.svg";
import { TERMS_URL } from "../../../config/runtime";

type StatusIconVariant = "lock" | "error";

interface StatusIconProps {
  variant: StatusIconVariant;
}

const StatusIcon: React.FC<StatusIconProps> = ({ variant }) => {
  if (variant === "error") {
    return (
      <div className="mx-auto mb-7 flex w-full max-w-[100px] items-center justify-center">
        <div className="flex h-[100px] w-[100px] items-center justify-center rounded-full bg-[#fff1f1]">
          <div className="flex h-[58px] w-[58px] items-center justify-center rounded-full bg-[#fde2e2]">
            <X
              className="h-8 w-8 text-destructive"
              strokeWidth={2.2}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto mb-7 flex w-full max-w-[100px] items-center justify-center">
      <div className="relative flex h-[118px] w-[118px] items-center justify-center rounded-full bg-[#eefaf7]">
        <LockKeyhole
          className="h-14 w-14 text-[#0b5c43]"
          strokeWidth={1.8}
        />
        <Sparkles className="absolute left-3 top-8 h-4 w-4 text-[#8fdcc7] opacity-90" />
        <Sparkles className="absolute right-4 top-6 h-3 w-3 text-[#8fdcc7] opacity-80" />
        <Sparkles className="absolute left-7 bottom-5 h-3.5 w-3.5 text-[#8fdcc7] opacity-75" />
        <Sparkles className="absolute right-7 bottom-7 h-4 w-4 text-[#8fdcc7] opacity-90" />
      </div>
    </div>
  );
};

export const ChangePasswordPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const navigate = useNavigate();
  const {
    form,
    loading,
    validating,
    displayError,
    retryAfterSeconds,
    passwordChanged,
    isTokenValid,
    submitResetPassword,
  } = useResetPassword(token);
  const {
    control,
    formState: { errors },
    handleSubmit,
  } = form;

  if (validating) {
    return (
      <div className="relative min-h-screen overflow-hidden bg-white text-[#0f172a]">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute inset-x-0 top-0 h-[64vh] bg-gradient-to-b from-[#f8fbfa] via-white to-white" />
          <img
            src={loginCircleLeft}
            alt=""
            aria-hidden="true"
            className="absolute left-0 bottom-16 w-[220px] opacity-35"
          />
          <img
            src={loginCircleRight}
            alt=""
            aria-hidden="true"
            className="absolute right-0 bottom-16 w-[220px] opacity-35"
          />
        </div>

        <header className="relative z-10 flex items-center justify-between px-6 py-5 md:px-8 md:py-7">
          <Link
            to="/login"
            className="shrink-0"
          >
            <img
              src={loginLogo}
              alt="CivicPoints"
              className="h-7 w-auto md:h-9"
            />
          </Link>
          <Link
            to="/login"
            className="rounded-md px-2 py-1 text-[15px] font-semibold text-[#0b5c43] transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            {t("auth.login")}
          </Link>
        </header>

        <main className="relative z-10 flex min-h-[calc(100vh-88px)] flex-col items-center justify-center px-5 pb-10 pt-8 md:px-8">
          <section className="w-full max-w-[600px] rounded-[26px] border border-[#e7ebf1] bg-white px-6 py-10 shadow-[0_24px_70px_rgba(15,23,42,0.10)] md:px-12 md:py-14">
            <StatusIcon variant="lock" />

            <div
              className="py-12 text-center"
              role="status"
              aria-live="polite"
            >
              <div className="flex justify-center mb-4">
                <LoaderCircle
                  className="h-12 w-12 animate-spin text-primary"
                  aria-hidden="true"
                />
              </div>
              <p className="text-sm md:text-base text-[#75757d] dark:text-[#a0a0a0]">
                {t("auth.reset_password_validating")}
              </p>
            </div>
          </section>
        </main>
      </div>
    );
  }

  if (displayError && !isTokenValid && !passwordChanged) {
    return (
      <div className="relative min-h-screen overflow-hidden bg-white text-[#0f172a]">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute inset-x-0 top-0 h-[64vh] bg-gradient-to-b from-[#f8fbfa] via-white to-white" />
          <img
            src={loginCircleLeft}
            alt=""
            aria-hidden="true"
            className="absolute left-0 bottom-16 w-[220px] opacity-35"
          />
          <img
            src={loginCircleRight}
            alt=""
            aria-hidden="true"
            className="absolute right-0 bottom-16 w-[220px] opacity-35"
          />
        </div>

        <header className="relative z-10 flex items-center justify-between px-6 py-5 md:px-8 md:py-7">
          <Link
            to="/login"
            className="shrink-0"
          >
            <img
              src={loginLogo}
              alt="CivicPoints"
              className="h-7 w-auto md:h-9"
            />
          </Link>
          <Link
            to="/login"
            className="rounded-md px-2 py-1 text-[15px] font-semibold text-[#0b5c43] transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            {t("auth.login")}
          </Link>
        </header>

        <main className="relative z-10 flex min-h-[calc(100vh-88px)] flex-col items-center justify-center px-5 pb-10 pt-8 md:px-8">
          <section className="w-full max-w-[600px] rounded-[26px] border border-[#e7ebf1] bg-white px-6 py-10 shadow-[0_24px_70px_rgba(15,23,42,0.10)] md:px-12 md:py-14">
            <StatusIcon variant="error" />

            <div className="py-12 text-center">
              <h1 className="mb-4 font-normal text-[24px] text-[#004231] md:text-[28px]">
                {t("auth.reset_password_invalid_title")}
              </h1>
              <p
                className="mb-4 text-sm text-destructive md:text-base"
                role="alert"
              >
                {displayError}
              </p>

              <Button
                type="button"
                onClick={() => navigate("/forgot-password")}
                className="h-11 w-full bg-primary text-primary-foreground hover:bg-primary/90 md:h-[46px]"
              >
                {t("auth.reset_password_back_to_forgot")}
              </Button>
            </div>
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-white text-[#0f172a]">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-x-0 top-0 h-[64vh] bg-gradient-to-b from-[#f8fbfa] via-white to-white" />
        <img
          src={loginCircleLeft}
          alt=""
          aria-hidden="true"
          className="absolute left-0 bottom-16 w-[220px] opacity-35"
        />
        <img
          src={loginCircleRight}
          alt=""
          aria-hidden="true"
          className="absolute right-0 bottom-16 w-[220px] opacity-35"
        />
      </div>

      <header className="relative z-10 flex items-center justify-between px-6 py-5 md:px-8 md:py-7">
        <Link
          to="/login"
          className="shrink-0"
        >
          <img
            src={loginLogo}
            alt="CivicPoints"
            className="h-7 w-auto md:h-9"
          />
        </Link>
        <Link
          to="/login"
          className="rounded-md px-2 py-1 text-[15px] font-semibold text-[#0b5c43] transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          {t("auth.login")}
        </Link>
      </header>

      <main className="relative z-10 flex min-h-[calc(100vh-88px)] flex-col items-center justify-center px-5 pb-10 pt-8 md:px-8">
        <section className="w-full max-w-[600px] rounded-[26px] border border-[#e7ebf1] bg-white px-6 py-10 shadow-[0_24px_70px_rgba(15,23,42,0.10)] md:px-12 md:py-14">
          <StatusIcon variant="lock" />

          {!passwordChanged ? (
            <>
              <div className="text-center">
                <h1 className="text-[30px] font-bold leading-tight text-[#0b5c43] md:text-[38px]">
                  {t("auth.change_password")}
                </h1>
              </div>

              <form
                onSubmit={handleSubmit(submitResetPassword)}
                className="mt-10 space-y-6"
              >
                <div>
                  <label
                    htmlFor="new-password"
                    className="mb-2.5 block text-sm font-semibold text-[#75757D] md:text-base"
                  >
                    {t("auth.new_password")}
                  </label>
                  <Controller
                    control={control}
                    name="newPassword"
                    render={({ field }) => (
                      <PasswordInput
                        id="new-password"
                        name={field.name}
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        disabled={loading}
                        error={errors.newPassword?.message}
                        required
                      />
                    )}
                  />
                  {errors.newPassword && (
                    <p
                      id="new-password-error"
                      role="alert"
                      className="mt-2 text-sm text-red-600 dark:text-red-400"
                    >
                      {errors.newPassword.message}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="confirm-password"
                    className="mb-2.5 block text-sm font-semibold text-[#75757d] md:text-base"
                  >
                    {t("auth.confirm_new_password")}
                  </label>
                  <Controller
                    control={control}
                    name="confirmPassword"
                    render={({ field }) => (
                      <PasswordInput
                        id="confirm-password"
                        name={field.name}
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        disabled={loading}
                        error={errors.confirmPassword?.message}
                        required
                      />
                    )}
                  />
                  {errors.confirmPassword && (
                    <p
                      id="confirm-password-error"
                      role="alert"
                      className="mt-2 text-sm text-red-600 dark:text-red-400"
                    >
                      {errors.confirmPassword.message}
                    </p>
                  )}
                </div>

                {displayError && (
                  <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3">
                    <p
                      className="text-center text-sm text-destructive"
                      role="alert"
                      aria-live="polite"
                    >
                      {displayError}
                    </p>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={loading || (retryAfterSeconds !== null && retryAfterSeconds > 0)}
                  className="h-11 w-full bg-primary text-primary-foreground hover:bg-primary/90 md:h-[46px]"
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <LoaderCircle
                        className="mr-2 h-4 w-4 animate-spin"
                        aria-hidden="true"
                      />
                      {t("auth.reset_password_changing")}
                    </div>
                  ) : retryAfterSeconds !== null && retryAfterSeconds > 0 ? (
                    t("auth.reset_password_wait", { seconds: retryAfterSeconds })
                  ) : (
                    t("auth.change_password")
                  )}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center">
              <h1 className="text-[30px] font-bold leading-tight text-[#0b5c43] md:text-[38px]">
                {t("auth.password_changed_successfully")}
              </h1>

              <p className="mx-auto mt-4 max-w-[460px] text-[16px] leading-7 text-[#6f7686] md:text-[17px]">
                {t("auth.reset_password_success_description")}
              </p>

              <Button
                type="button"
                onClick={() => navigate("/login")}
                className="mt-10 h-11 w-full bg-primary text-primary-foreground hover:bg-primary/90 md:h-[46px]"
              >
                {t("auth.login")}
              </Button>
            </div>
          )}
        </section>

        <footer className="mt-14 flex flex-wrap justify-center gap-x-3 gap-y-1 text-center text-[14px] text-[#7a8192] md:mt-16">
          <a
            href={TERMS_URL}
            className="rounded-md px-1 py-1 transition-colors hover:text-[#0b5c43] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {t("auth.footer_terms")}
          </a>
          <span
            className="hidden text-[#c5cad4] sm:inline"
            aria-hidden="true"
          >
            |
          </span>
          <a
            href="https://civicpoints.io/privacy-policy"
            className="rounded-md px-1 py-1 transition-colors hover:text-[#0b5c43] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {t("auth.footer_privacy")}
          </a>
          <span
            className="hidden text-[#c5cad4] sm:inline"
            aria-hidden="true"
          >
            |
          </span>
          <a
            href="https://helpdesk.civicpoints.io/en/customer/create-ticket/"
            className="rounded-md px-1 py-1 transition-colors hover:text-[#0b5c43] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {t("auth.footer_help")}
          </a>
        </footer>
      </main>
    </div>
  );
};

export default ChangePasswordPage;
